import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import './KioskPage.scss';

// The self-service kiosk screen, shown full-screen by the MyRoomHQ Kiosk Android app. It talks to the
// tablet through window.MyRoomKiosk (camera, printer, scanner) and to the API with the kiosk's own
// device token — no guest is signed in here. Paying: "Charge to my room" shows a QR the guest scans
// with their phone (that charges their folio), or "Pay by card" on the station's EMV terminal.

interface KioskBridge {
  getConfig(): string;
  capturePhoto(callbackId: string): void;
  print(callbackId: string, receiptJson: string): void;
}

declare global {
  interface Window {
    MyRoomKiosk?: KioskBridge;
    __kioskCallback?: (id: string, result: unknown) => void;
    onKioskScan?: (code: string) => void;
  }
}

interface KioskItem { id: number; name: string; price?: number; imagePath?: string; barcode?: string; soldOut: boolean }
interface KioskCategory { id: number; name: string; items: KioskItem[] }
interface KioskSession {
  kioskName: string; stationName: string; stationIcon?: string; currencySymbol: string;
  roomCharge: boolean; cardPayment: boolean; categories: KioskCategory[];
}
interface ScanResult {
  scanId: string; success: boolean; errorCode?: string; error?: string;
  items: { menuItemId: number; quantity: number; confidence?: string }[];
  possible: { quantity: number; note?: string; candidateMenuItemIds: number[] }[];
  unrecognized: { description: string; quantity: number }[];
}
interface Checkout {
  id: string; status: string; method?: string; payUrl?: string; total: number; currencySymbol: string;
  chargedTotal: number; chargedCurrencySymbol: string; orderNumber?: string; error?: string; expiresAt: string;
  lines: { menuItemId: number; name: string; quantity: number; lineTotal: number }[];
}

type Screen = 'shop' | 'photos' | 'scanResult' | 'qr' | 'card' | 'paid' | 'failed';

const IDLE_MS = 90_000;

// Bridge calls are async on the native side; each gets a callback id.
const pending = new Map<string, (r: any) => void>();
window.__kioskCallback = (id, result) => { pending.get(id)?.(result); pending.delete(id); };
const callBridge = <T,>(fn: (id: string) => void) => new Promise<T>(resolve => {
  const id = `${Date.now()}-${Math.random()}`;
  pending.set(id, resolve);
  fn(id);
});

export default function KioskPage() {
  const bridge = window.MyRoomKiosk;
  const config = useMemo(() => {
    try { return bridge ? JSON.parse(bridge.getConfig()) as { apiBase: string; token: string; printer: boolean } : null; }
    catch { return null; }
  }, [bridge]);

  const [session, setSession] = useState<KioskSession | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cart, setCart] = useState<Map<number, number>>(new Map());
  const [screen, setScreen] = useState<Screen>('shop');
  const [photos, setPhotos] = useState<{ captureId: string; preview: string }[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [scan, setScan] = useState<ScanResult | null>(null);
  const [scanId, setScanId] = useState<string | null>(null);
  const [picks, setPicks] = useState<Record<number, number | null>>({});
  const [checkout, setCheckout] = useState<Checkout | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const lastTouch = useRef(Date.now());
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const api = useCallback(async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
    const res = await fetch(`${config!.apiBase}${path}`, {
      ...init,
      headers: { Authorization: `Bearer ${config!.token}`, 'Content-Type': 'application/json', ...(init.headers || {}) },
    });
    const body = res.status === 204 ? null : await res.json().catch(() => null);
    if (!res.ok && !(body && body.status)) throw new Error(body?.error || body?.Error || `Error ${res.status}`);
    return body as T;
  }, [config]);

  const items = useMemo(() => new Map((session?.categories ?? []).flatMap(c => c.items).map(i => [i.id, i])), [session]);
  const sym = session?.currencySymbol ?? '₪';
  const cartLines = [...cart.entries()].filter(([, q]) => q > 0).map(([id, q]) => ({ item: items.get(id)!, quantity: q })).filter(l => l.item);
  const cartTotal = cartLines.reduce((s, l) => s + (l.item.price ?? 0) * l.quantity, 0);

  const showToast = (text: string) => { setToast(text); setTimeout(() => setToast(null), 2500); };

  const stopPolling = () => { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; };

  const reset = useCallback(() => {
    stopPolling();
    setCart(new Map());
    setPhotos([]);
    setScan(null);
    setScanId(null);
    setPicks({});
    setCheckout(null);
    setBusy(null);
    setScreen('shop');
  }, []);

  // Load the kiosk's station menu
  useEffect(() => {
    if (!config) return;
    api<KioskSession>('/api/v1/kiosk/session').then(setSession).catch(e => setLoadError(e.message));
  }, [config, api]);

  // Barcode scanner → add the item
  useEffect(() => {
    window.onKioskScan = async (code: string) => {
      lastTouch.current = Date.now();
      if (screen !== 'shop') return;
      try {
        const res = await api<{ menuItemId: number }>(`/api/v1/kiosk/barcode/${encodeURIComponent(code)}`);
        add(res.menuItemId, 1);
        showToast(`Added ${items.get(res.menuItemId)?.name ?? ''}`);
      } catch (e) {
        showToast((e as Error).message);
      }
    };
    return () => { window.onKioskScan = undefined; };
  }, [api, screen, items]);

  // Walk away mid-shop → the next person starts fresh
  useEffect(() => {
    const touch = () => { lastTouch.current = Date.now(); };
    window.addEventListener('pointerdown', touch);
    const timer = setInterval(() => {
      if ((screen === 'shop' || screen === 'photos' || screen === 'scanResult' || screen === 'failed')
          && Date.now() - lastTouch.current > IDLE_MS && (cart.size > 0 || photos.length > 0 || screen !== 'shop')) reset();
    }, 5000);
    return () => { window.removeEventListener('pointerdown', touch); clearInterval(timer); };
  }, [screen, cart, photos, reset]);

  const add = (id: number, n: number) => setCart(prev => {
    const next = new Map(prev);
    next.set(id, Math.max(0, (next.get(id) ?? 0) + n));
    if (next.get(id) === 0) next.delete(id);
    return next;
  });

  // ── Camera ──

  const takePhoto = async () => {
    if (!bridge) return;
    setBusy('Taking the photo…');
    const r = await callBridge<{ ok: boolean; captureId?: string; preview?: string; error?: string }>(id => bridge.capturePhoto(id));
    setBusy(null);
    if (!r.ok || !r.captureId) { showToast(r.error || 'The camera did not respond'); return; }
    setPhotos(p => [...p, { captureId: r.captureId!, preview: r.preview ?? '' }].slice(0, 6));
    setScreen('photos');
  };

  const checkItems = async () => {
    setBusy('Checking your items…');
    try {
      const r = await api<ScanResult>('/api/v1/kiosk/scan', {
        method: 'POST', body: JSON.stringify({ captureIds: photos.map(p => p.captureId) }),
      });
      setBusy(null);
      if (!r.success) {
        showToast(r.errorCode === 'unreadable' ? 'We couldn’t make out the photo — lay the items flat and try again.' : (r.error || 'Scan failed'));
        return;
      }
      setScan(r);
      setScanId(r.scanId);
      setPicks({});
      setScreen('scanResult');
    } catch (e) {
      setBusy(null);
      showToast((e as Error).message);
    }
  };

  // A scan replaces the cart — every photo of the basket is sent each time, so nothing is counted twice.
  const useScan = () => {
    if (!scan) return;
    const next = new Map<number, number>();
    scan.items.filter(i => items.has(i.menuItemId)).forEach(i => next.set(i.menuItemId, (next.get(i.menuItemId) ?? 0) + i.quantity));
    scan.possible.forEach((p, idx) => {
      const pick = picks[idx];
      if (pick != null) next.set(pick, (next.get(pick) ?? 0) + p.quantity);
    });
    setCart(next);
    setScreen('shop');
  };

  // ── Paying ──

  const createCheckout = async () => api<Checkout>('/api/v1/kiosk/checkouts', {
    method: 'POST',
    body: JSON.stringify({ lines: cartLines.map(l => ({ menuItemId: l.item.id, quantity: l.quantity })), scanId }),
  });

  const payByRoom = async () => {
    setBusy('One moment…');
    try {
      const c = await createCheckout();
      setCheckout(c);
      setBusy(null);
      setScreen('qr');
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const latest = await api<Checkout>(`/api/v1/kiosk/checkouts/${c.id}`);
          setCheckout(latest);
          if (latest.status === 'paid') { stopPolling(); onPaid(latest); }
          else if (latest.status === 'failed' || latest.status === 'expired' || latest.status === 'cancelled') { stopPolling(); setScreen('failed'); }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (e) {
      setBusy(null);
      showToast((e as Error).message);
    }
  };

  const payByCard = async () => {
    setBusy('One moment…');
    try {
      const c = await createCheckout();
      setCheckout(c);
      setBusy(null);
      setScreen('card');
      const result = await api<Checkout>(`/api/v1/kiosk/checkouts/${c.id}/card`, { method: 'POST' });
      setCheckout(result);
      if (result.status === 'paid') onPaid(result); else setScreen('failed');
    } catch (e) {
      setBusy(null);
      setCheckout(prev => prev ? { ...prev, error: (e as Error).message } : prev);
      setScreen('failed');
    }
  };

  const cancelCheckout = async () => {
    stopPolling();
    if (checkout) await api(`/api/v1/kiosk/checkouts/${checkout.id}/cancel`, { method: 'POST' }).catch(() => null);
    setCheckout(null);
    setScreen('shop');
  };

  const printReceipt = async (c: Checkout) => {
    if (!bridge || !config?.printer) return;
    const receipt = {
      title: session?.stationName ?? 'Receipt',
      orderNumber: c.orderNumber,
      currency: c.chargedCurrencySymbol,
      lines: c.lines.map(l => ({ name: l.name, qty: l.quantity, amount: l.lineTotal })),
      total: c.chargedTotal,
      footer: c.method === 'card' ? 'Paid by card — thank you!' : 'Charged to your room — thank you!',
    };
    const r = await callBridge<{ ok: boolean; error?: string }>(id => bridge.print(id, JSON.stringify(receipt)));
    if (!r.ok) showToast(r.error || 'Printer error');
  };

  const onPaid = (c: Checkout) => {
    setScreen('paid');
    printReceipt(c);
    setTimeout(reset, 12_000);
  };

  // ── Render ──

  if (!bridge || !config) {
    return <div className="kiosk kiosk-center"><h1>MyRoomHQ Kiosk</h1><p>This page runs inside the MyRoomHQ Kiosk app.</p></div>;
  }
  if (loadError) {
    return <div className="kiosk kiosk-center"><h1>Kiosk unavailable</h1><p>{loadError}</p><button className="k-btn" onClick={() => location.reload()}>Try again</button></div>;
  }
  if (!session) return <div className="kiosk kiosk-center"><div className="k-spinner" /></div>;

  const unanswered = (scan?.possible ?? []).some((_, i) => !(i in picks));

  return (
    <div className="kiosk">
      <header className="k-header">
        <h1>{session.stationIcon || '🛒'} {session.stationName}</h1>
        {(cart.size > 0 || screen !== 'shop') && <button className="k-link" onClick={reset}>Start over</button>}
      </header>

      <main className="k-main">
        <section className="k-menu">
          <button className="k-scan-hero" onClick={takePhoto}>
            <span className="k-scan-icon">📷</span>
            <span><strong>Scan your items</strong><small>Put everything under the camera — or tap items below, or scan a barcode</small></span>
          </button>
          {session.categories.map(cat => (
            <div key={cat.id} className="k-category">
              <h2>{cat.name}</h2>
              <div className="k-grid">
                {cat.items.map(item => (
                  <button key={item.id} className="k-tile" disabled={item.soldOut} onClick={() => add(item.id, 1)}>
                    {item.imagePath ? <img src={item.imagePath} alt="" /> : <div className="k-tile-ph">🛍️</div>}
                    <span className="k-tile-name">{item.name}</span>
                    <span className="k-tile-price">{item.soldOut ? 'Sold out' : item.price ? `${sym}${item.price.toFixed(2)}` : 'Free'}</span>
                    {(cart.get(item.id) ?? 0) > 0 && <span className="k-badge">{cart.get(item.id)}</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </section>

        <aside className="k-cart">
          <h2>Your items</h2>
          {cartLines.length === 0 ? <p className="k-muted">Nothing yet.</p> : cartLines.map(l => (
            <div key={l.item.id} className="k-line">
              <span className="k-line-name">{l.item.name}</span>
              <div className="k-qty">
                <button onClick={() => add(l.item.id, -1)}>−</button>
                <span>{l.quantity}</span>
                <button onClick={() => add(l.item.id, 1)}>+</button>
              </div>
              <span className="k-line-total">{l.item.price ? `${sym}${(l.item.price * l.quantity).toFixed(2)}` : '—'}</span>
            </div>
          ))}
          <div className="k-total"><span>Total</span><span>{sym}{cartTotal.toFixed(2)}</span></div>
          {session.roomCharge && (
            <button className="k-btn primary" disabled={cartLines.length === 0} onClick={payByRoom}>🏨 Charge to my room</button>
          )}
          {session.cardPayment && (
            <button className="k-btn" disabled={cartLines.length === 0 || cartTotal <= 0} onClick={payByCard}>💳 Pay by card</button>
          )}
          {!session.roomCharge && !session.cardPayment && <p className="k-muted">Payment isn’t available at this kiosk right now.</p>}
        </aside>
      </main>

      {screen === 'photos' && (
        <div className="k-overlay"><div className="k-sheet">
          <h2>Your photos</h2>
          <div className="k-photos">{photos.map(p => <img key={p.captureId} src={p.preview} alt="" />)}</div>
          <p className="k-muted">Something hidden? Move it and take another photo — we count each item once.</p>
          <div className="k-actions">
            <button className="k-btn" disabled={photos.length >= 6} onClick={takePhoto}>📷 Another photo</button>
            <button className="k-btn primary" onClick={checkItems}>🔍 Check my items</button>
            <button className="k-link" onClick={() => { setPhotos([]); setScreen('shop'); }}>Cancel</button>
          </div>
        </div></div>
      )}

      {screen === 'scanResult' && scan && (
        <div className="k-overlay"><div className="k-sheet">
          <h2>We found</h2>
          {scan.items.filter(i => items.has(i.menuItemId)).map(i => (
            <div key={i.menuItemId} className="k-line">
              <span className="k-line-name">{i.quantity}× {items.get(i.menuItemId)!.name}</span>
              {i.confidence !== 'high' && <span className="k-warn">please check</span>}
            </div>
          ))}
          {scan.possible.map((p, idx) => (
            <div key={idx} className="k-question">
              <p>{p.quantity > 1 ? `${p.quantity}× ` : ''}Which one is this?{p.note ? ` (${p.note})` : ''}</p>
              <div className="k-choices">
                {p.candidateMenuItemIds.filter(id => items.has(id)).map(id => (
                  <button key={id} className={`k-choice ${picks[idx] === id ? 'on' : ''}`} onClick={() => setPicks(x => ({ ...x, [idx]: id }))}>{items.get(id)!.name}</button>
                ))}
                <button className={`k-choice ${picks[idx] === null ? 'on' : ''}`} onClick={() => setPicks(x => ({ ...x, [idx]: null }))}>None of these</button>
              </div>
            </div>
          ))}
          {scan.unrecognized.length > 0 && (
            <p className="k-warn-box">Not sold here: {scan.unrecognized.map(u => u.description || 'an item').join(', ')}</p>
          )}
          <div className="k-actions">
            <button className="k-btn primary" disabled={unanswered} onClick={useScan}>{unanswered ? 'Choose above first' : 'Use these items'}</button>
            <button className="k-btn" onClick={takePhoto}>📷 Add a photo</button>
          </div>
        </div></div>
      )}

      {screen === 'qr' && checkout?.payUrl && (
        <div className="k-overlay"><div className="k-sheet k-center">
          <h2>Scan with your phone</h2>
          <p className="k-muted">Use the phone you use for the hotel app — the purchase is charged to your room.</p>
          <div className="k-qr"><QRCodeSVG value={checkout.payUrl} size={260} /></div>
          <div className="k-total big"><span>Total</span><span>{checkout.currencySymbol}{checkout.total.toFixed(2)}</span></div>
          <button className="k-link" onClick={cancelCheckout}>Cancel</button>
        </div></div>
      )}

      {screen === 'card' && checkout && (
        <div className="k-overlay"><div className="k-sheet k-center">
          <div className="k-big-icon">💳</div>
          <h2>Tap or insert your card</h2>
          <p className="k-muted">on the card terminal</p>
          <div className="k-total big"><span>Total</span><span>{checkout.currencySymbol}{checkout.total.toFixed(2)}</span></div>
        </div></div>
      )}

      {screen === 'paid' && checkout && (
        <div className="k-overlay"><div className="k-sheet k-center">
          <div className="k-big-icon">✅</div>
          <h2>Thank you!</h2>
          <p>{checkout.method === 'card' ? 'Paid by card' : 'Charged to your room'} · {checkout.chargedCurrencySymbol}{checkout.chargedTotal.toFixed(2)}</p>
          {config.printer && <button className="k-btn" onClick={() => printReceipt(checkout)}>🧾 Print receipt again</button>}
          <button className="k-btn primary" onClick={reset}>Done</button>
        </div></div>
      )}

      {screen === 'failed' && (
        <div className="k-overlay"><div className="k-sheet k-center">
          <div className="k-big-icon">⚠️</div>
          <h2>{checkout?.status === 'expired' ? 'That code expired' : 'Payment didn’t go through'}</h2>
          {checkout?.error && <p className="k-muted">{checkout.error}</p>}
          <button className="k-btn primary" onClick={() => { setCheckout(null); setScreen('shop'); }}>Back</button>
        </div></div>
      )}

      {busy && <div className="k-overlay"><div className="k-sheet k-center"><div className="k-spinner" /><p>{busy}</p></div></div>}
      {toast && <div className="k-toast">{toast}</div>}
    </div>
  );
}
